'use client';

import 'react-toggle/style.css';

import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    SheetTrigger
} from '@/components/ui/sheet';
import {
    GPTModels,
    DEFAULT_MODEL
} from '@/types/openaiModels';
import { getHumorSettings, numberToHumorLevel } from '@/types/humor';
import {
    getVerbositySettings,
    numberToVerbosityLevel
} from '@/types/verbosity';
import { useEffect, useState, useCallback } from 'react';
import React from 'react';

import { Button } from './ui/button';
import { DevMenu } from './github-menu';
import { IconSettings } from './ui/icons';
import { Input } from './ui/input';
import { Preferences, defaultPreferences } from '@/types/preferences';
import { ThemeToggle } from './theme-toggle';
import { useLogger } from 'next-axiom';

// Define the static list of models with display names
const staticModels: { id: GPTModels; displayName: string }[] = [
    { id: 'gpt-4o', displayName: 'GPT-4o' },
    { id: 'gpt-4o-mini', displayName: 'GPT-4o Mini' },
    { id: 'gpt-4-turbo', displayName: 'GPT-4 Turbo' },
    { id: 'gpt-4', displayName: 'GPT-4' },
    { id: 'gpt-3.5-turbo', displayName: 'GPT-3.5 Turbo' },
];

// Single localStorage key for everyone
const LOCAL_STORAGE_KEY = 'chauncey-preferences';

const UserPreferences = () => {
    const log = useLogger();
    const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false);
    const [preferences, setPreferencesState] = useState<Preferences>(defaultPreferences);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [preferredLanguage, setPreferredLanguage] = useState<string>(defaultPreferences.language);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);

    // Memoized function to save to localStorage
    const saveToLocalStorage = useCallback((prefs: Preferences) => {
        try {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(prefs));
        } catch (e) {
            console.error('Failed to save preferences to localStorage', e);
            log.error('Failed to save preferences to localStorage', { e });
        }
    }, [log]);

    // Load preferences from localStorage only once on mount
    useEffect(() => {
        if (isInitialized) return;

        try {
            setIsLoading(true);
            const storedPrefs = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (storedPrefs) {
                try {
                    const parsedPrefs = JSON.parse(storedPrefs) as Preferences;
                    setPreferencesState(parsedPrefs);
                    setPreferredLanguage(parsedPrefs.language || defaultPreferences.language);
                } catch (parseError) {
                    log.error('Error parsing preferences', { parseError });
                }
            }
        } catch (e) {
            log.error('Error loading preferences from localStorage', { e });
        } finally {
            setIsLoading(false);
            setIsInitialized(true);
        }
    }, [isInitialized, log]);

    // Safe wrapper to update preferences
    const setPreferences = useCallback((newPreferences: Preferences) => {
        setPreferencesState(newPreferences);
        saveToLocalStorage(newPreferences);
    }, [saveToLocalStorage]);

    const handleGPTPreferenceChange = useCallback((model: GPTModels) => {
        setPreferences({
            ...preferences,
            gptModel: model
        });
    }, [preferences, setPreferences]);

    const handleVerbosityChange = useCallback((verbosity: string) => {
        try {
            const verbosityNumber = parseInt(verbosity);
            const v = numberToVerbosityLevel(verbosityNumber);
            setPreferences({ ...preferences, verbosity: v });
        } catch (e) {
            log.error('verbosity error', { e });
        }
    }, [preferences, setPreferences, log]);

    const handleHumorChange = useCallback((humor: string) => {
        try {
            const num = parseInt(humor);
            const h = numberToHumorLevel(num);
            setPreferences({ ...preferences, humor: h });
        } catch (e) {
            log.error('humor error haha', { e });
        }
    }, [preferences, setPreferences, log]);

    const handleLanguageChange = useCallback((language: string) => {
        try {
            setPreferences({ ...preferences, language });
        } catch (e) {
            log.error('language error', { e });
        }
    }, [preferences, setPreferences, log]);

    // Handle language input change separately to avoid excessive updates
    const onLanguageInputChange = useCallback((value: string) => {
        setPreferredLanguage(value);
    }, []);

    // Only update actual preference on blur
    const onLanguageBlur = useCallback((value: string) => {
        handleLanguageChange(value);
    }, [handleLanguageChange]);

    // Don't render form until preferences are loaded
    if (!isInitialized && isLoading) {
        return (
            <div>
                <Button variant="ghost" className="-ml-2 h-9 w-9 p-0">
                    <IconSettings className="h-6 w-6" />
                </Button>
            </div>
        );
    }

    return (
        <div>
            <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                <SheetTrigger asChild>
                    <Button variant="ghost" className="-ml-2 h-9 w-9 p-0">
                        <IconSettings className="h-6 w-6" />
                    </Button>
                </SheetTrigger>
                <SheetContent className="flex flex-col justify-between">
                    <div>
                        <SheetHeader className="mb-4">
                            <SheetTitle>User Preferences</SheetTitle>
                            <SheetDescription>
                                Update your personal preferences here.
                            </SheetDescription>
                        </SheetHeader>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <label
                                    htmlFor="darkMode"
                                    className="font-medium"
                                >
                                    Dark Mode
                                </label>
                                <ThemeToggle />
                            </div>
                            <div className="flex items-center justify-between">
                                <label
                                    htmlFor="gptModel"
                                    className="font-medium"
                                >
                                    GPT Model
                                </label>
                                <select
                                    id="gptModel"
                                    value={preferences.gptModel || DEFAULT_MODEL}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                        handleGPTPreferenceChange(
                                            e.target.value as GPTModels
                                        )
                                    }
                                    className="mt-1 rounded border border-gray-300 bg-background p-2 px-3 py-1 leading-8 outline-none transition-colors duration-200 ease-in-out focus:border-indigo-500 focus:ring-indigo-500"
                                >
                                    {staticModels.map(model => (
                                        <option key={model.id} value={model.id}>
                                            {model.displayName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex items-center justify-between">
                                <label
                                    htmlFor="verbosity"
                                    className="font-medium"
                                >
                                    Verbosity Level
                                </label>
                                <select
                                    id="verbosity"
                                    value={preferences.verbosity || 3}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                        handleVerbosityChange(e.target.value)
                                    }
                                    className="mt-1 rounded border border-gray-300 bg-background p-2 px-3 py-1 leading-8 outline-none transition-colors duration-200 ease-in-out focus:border-indigo-500 focus:ring-indigo-500"
                                >
                                    <option value={1}>1</option>
                                    <option value={2}>2</option>
                                    <option value={3}>3</option>
                                    <option value={4}>4</option>
                                    <option value={5}>5</option>
                                </select>
                            </div>
                            {preferences.verbosity && (
                                <p className="text-sm text-gray-500">
                                    {
                                        getVerbositySettings(
                                            preferences.verbosity
                                        ).description
                                    }
                                </p>
                            )}
                            <div className="flex items-center justify-between">
                                <label
                                    htmlFor="humor"
                                    className="font-medium"
                                >
                                    Humor Level
                                </label>
                                <select
                                    id="humor"
                                    value={preferences.humor || 2}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                        handleHumorChange(e.target.value)
                                    }
                                    className="mt-1 rounded border border-gray-300 bg-background p-2 px-3 py-1 leading-8 outline-none transition-colors duration-200 ease-in-out focus:border-indigo-500 focus:ring-indigo-500"
                                >
                                    <option value={1}>1</option>
                                    <option value={2}>2</option>
                                    <option value={3}>3</option>
                                    <option value={4}>4</option>
                                    <option value={5}>5</option>
                                </select>
                            </div>
                            {preferences.humor && (
                                <p className="text-sm text-gray-500">
                                    {
                                        getHumorSettings(preferences.humor)
                                            .description
                                    }
                                </p>
                            )}
                            <div className="flex items-center justify-between">
                                <label
                                    htmlFor="language"
                                    className="font-medium"
                                >
                                    Preferred Language
                                </label>
                                <Input
                                    type="text"
                                    id="language"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        onLanguageInputChange(e.target.value)
                                    }
                                    onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                                        onLanguageBlur(e.target.value)
                                    }
                                    value={preferredLanguage}
                                    className="mt-1 rounded border border-gray-300 bg-background p-2 px-3 py-1 leading-8 outline-none transition-colors duration-200 ease-in-out focus:border-indigo-500 focus:ring-indigo-500"
                                />
                            </div>
                            {preferences.language && (
                                <p className="text-sm text-gray-500">
                                    Type the entire language name, e.g. Spanish
                                </p>
                            )}
                        </div>
                    </div>
                    <SheetFooter>
                        <DevMenu />
                    </SheetFooter>
                </SheetContent>
            </Sheet>
        </div>
    );
};

export default UserPreferences;
